import { SortingState } from '@tanstack/react-table';
import { useQuery } from 'react-query';
import { network } from 'services/network';
import { NarrativeRequest } from 'utilitys/narrative/narrativeTools';

import { Aggregation } from 'store/productivityRate.store';

import { QueryKeys } from 'api/queryKeys';

import { RangeWithCustom } from 'components/common/GlobalFilterComponent/components/dateRangeFilter/dateRangeFilter';
import { ICardLastComment } from 'components/common/Tables/Cells/DetailsCell';
import { Pagination } from 'components/common/pagination/customPagination';

import {
    IAssignee,
    ICardIndicators,
    ICategory,
    IStatus,
    ITagList,
} from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/battlecards.types';
import { CardType } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/helper';
import { IFilter } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';

const queryFn = (params: QueryParams) => {
    return network.post('api/bo/getRunRateAnalysisBreakdown', params).then((response) => {
        return response.data;
    });
};

export const useQueryGetRunRateAnalysisBreakdownQuery = (params: QueryParams) => {
    return useQuery<QueryResponse, QueryError, QueryResponse, [QueryKeys, unknown]>({
        queryKey: ['getRunRateAnalysisBreakdownQuery', params],
        queryFn: () => queryFn(params),
        enabled:
            Boolean(params.actualMetaDataId) &&
            Boolean(params.comparedMetaDataId) &&
            Boolean(params.startDate) &&
            Boolean(params.endDate),
        cacheTime: 0,
    });
};

interface QueryParams extends NarrativeRequest {
    actualMetaDataId: number | undefined;
    comparedMetaDataId: number;
    startDate: number | null;
    endDate: number | null;
    range: RangeWithCustom;
    filter: {
        filters: IFilter[];
    };
    dataType: string | null;
    id: string[];
    sortingList: SortingState;
    paging: Pagination;
    aggregation: Aggregation | null;
}

export interface QueryResponse {
    cardList: {
        id: number;
        metaDataId: number;
        criticalityScore: number;
        float: number;
        taskHashCode: string;
        taskVersionHashCode: string;
        plannedStartDate: number;
        plannedFinishDate: number;
        actualStartDate: number;
        actualFinishDate: number | null;
        numOfPredecessors: number;
        numOfSuccessors: number;
        projectWeekStart: number;
        projectWeekFinish: number;
        projectMonthStart: number;
        projectMonthFinish: number;
        status: IStatus;
        originalStatus: IStatus;
        taskActivityId: string;
        parentActivity: string;
        grandParentActivity: string;
        plannedDurationInDays: number;
        actualDurationInDays: number;
        remainingDurationInDays: number;
        completedDurationInDays: number;
        taskActivityType: string;
        isVirtual: boolean;
        category: ICategory;
        title: string;
        startDate: number;
        endDate: number;
        dueDate: number | null;
        duration: number;
        rank: number;
        tagList: ITagList[];
        versionDate: number;
        isStartOverdue: boolean;
        isFinishOverdue: boolean;
        taskId: number;
        startVariance: number;
        finishVariance: number;
        currentDurationOverrun: number;
        completedDurationOverrun: number;
        isInterimChanged: boolean;
        overrunPercent: number;
        assignee: IAssignee | null;
        details: string;
        cardLastComment: ICardLastComment;
        cardIndicators: ICardIndicators;
        submittedDurationComplete: number;
        isTracked: boolean;
        precedingActivities: number;
        succeedingActivites: number;
        precedingRisk: string;
        succeedingRisk: string;
        type: {
            id: number;
            name: CardType;
            label: string;
        };
        baselineStartDate: number;
        baselineFinishDate: number;
        orderInPage: number;
        uniqId: number;
        allBudgetResourceUnit: null;
        allActualResourceUnit: null;
        allResourceNameList: string[];
        budgetResourceUnit: number;
        actualResourceUnit: number;
        remainingResourceUnit: number;
        resourceName: string;
        resourceType: string;
        isGenAiDetails: boolean;
    }[];
    numOfAllCards: number;
    numOfPages: number;
    info: IToolTip;
}
